import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import { validateField, validateOptionalField, DESCRIPTION_MAX_LENGTH, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { RequestFormStyle } from '../styles'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { createCodeNotebook } from '../../reducers/codenotebooksReducer'
import useResourceSubmit from '../../hooks/useResourceSubmit'

const CodeNotebookForm = () => {

    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [language, setLanguage] = useState('PY')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])


    const reset = () => {
        setName('')
        setNameError(false)
        setNameHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setAuthors('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLink('')
        setLinkError(false)
        setLinkHelper('')
        setDate('')
        setDateError(false)
        setDateHelper('')
        setContentTags([])
        setMedicalTags([])
        setAdminCategoryTags([])
        setExperienceTags([])
        setDemographicTags([])
        setShowTagErrors(false)
        setIsSeminalWork(false)
        setInQuiz(false)
        setIsTcairem(false)
        setIsPaid(false)
        setTcairemTags([])
        setPersonaTags([])
        setLanguage('PY')
    }


    const handleSubmit = (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (
                validateField(name, setNameError, setNameHelper, 200, 'Title') &&
                validateOptionalField(description, setDescriptionError, setDescriptionHelper, DESCRIPTION_MAX_LENGTH, 'Description') &&
                validateField(authors, setAuthorsError, setAuthorsHelper, 100, 'Authors') &&
                validateField(link, setLinkError, setLinkHelper) &&
                validateField(date, setDateError, setDateHelper) &&
                validateRequiredResourceTags({
                    contentTags,
                    adminCategoryTags,
                    experienceTags,
                    demographicTags,
                    tcairemTags,
                    personaTags,
                    isTcairem,
                })
            ) {

            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                language: language,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }


            submit(createCodeNotebook(data), reset)
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    return (
        <>
            <Container {...RequestFormStyle.container}>
                <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                    Code Notebook Form
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={language}
                                select
                                label="Programming Language"
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                <MenuItem value="PY">Python</MenuItem>
                                <MenuItem value="R">R</MenuItem>
                                <MenuItem value="ML">MATLAB</MenuItem>
                                <MenuItem value="C">C</MenuItem>
                                <MenuItem value="CS">C#</MenuItem>
                                <MenuItem value="CP">C++</MenuItem>
                                <MenuItem value="JS">JavaScript</MenuItem>
                                <MenuItem value="JL">Julia</MenuItem>
                                <MenuItem value="JV">Java</MenuItem>
                                <MenuItem value="SC">Scala</MenuItem>
                                <MenuItem value="SQ">SQL</MenuItem>
                                <MenuItem value="OT">Other</MenuItem>
                            </TextField>
                        </Grid>
                        <CoreResourceFields
                            name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                            authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                            link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                            date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                            validate={validateField}
                            nameLabel="Title"
                            authorsLabel="Authors"
                            dateLabel="Date Updated"
                            showCharCount={true}
                            nameMaxLength={200}
                            authorsMaxLength={100}
                        />
                        <ResourceDescriptionField
                            value={description} setValue={setDescription}
                            error={descriptionError} setError={setDescriptionError}
                            helper={descriptionHelper} setHelper={setDescriptionHelper}
                        />
                        <ResourceTagsSection
                            contentTags={contentTags} setContentTags={setContentTags}
                            medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                            adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                            experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                            required={true}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                        <TCAIREMFieldsSection
                            isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                            isPaid={isPaid} setIsPaid={setIsPaid}
                            tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                            personaTags={personaTags} setPersonaTags={setPersonaTags}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4}>
                            <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>Create Code Notebook</Button>
                        </Grid>
                    </Grid>
                </form>
            </Container>
        </>
    )
}

export default CodeNotebookForm
